/*
 * Peel — the devices a product is made for.
 *
 * Compatibility is declared on the product, not inferred from its name:
 * three list metafields say which phones a product fits, one per brand.
 *
 *   custom.iphone_model   ["iPhone 17 Pro"]              -> Apple
 *   custom.pixel_model    ["Google Pixel 10 Pro"]        -> Google
 *   custom.samsung_model  ["Samsung Galaxy S26 Ultra"]   -> Samsung
 *
 * They're lists, so a product cut for two phones (lens protectors, say)
 * declares both, and a product that fits no phone declares nothing — which
 * is what keeps chargers, bags and AirPods cases out of the picker.
 *
 * Liquid renders the values; this file turns them into the brand -> model
 * catalogue the pickers show, and into the keys the fit filter matches on.
 * The keys must agree with the ones Liquid writes onto each card, so the
 * slug here mirrors `replace: '+', ' plus' | handleize`.
 */
(function () {
  /* Brands in the order the picker lists them. `prefix` is the brand word
     the metafield values repeat — dropped from the label, because the brand
     is already the other half of the picker. */
  var BRANDS = [
    { name: 'Apple', metafield: 'iphone_model', prefix: '' },
    { name: 'Google', metafield: 'pixel_model', prefix: 'Google ' },
    { name: 'Samsung', metafield: 'samsung_model', prefix: 'Samsung ' }
  ];

  function slug(value) {
    return String(value)
      .replace(/\+/g, ' plus')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  function label(value, prefix) {
    var text = String(value).trim();
    if (prefix && text.toLowerCase().indexOf(prefix.toLowerCase()) === 0) {
      text = text.slice(prefix.length);
    }
    return text;
  }

  /* Newest first, and within a series: Ultra, Pro Max, Pro, Plus, base,
     Air, Mini, e. Peel's oldest lines (iPhone X, Galaxy Note) sort below
     the numbered ones. */
  function weight(model) {
    var lower = model.toLowerCase().replace(/\+/g, ' plus');
    var match = lower.match(/(\d+)/);
    var series = match ? parseInt(match[1], 10) : 0;

    if (/\bx[rs]?\b/.test(lower) && !match) series = 10;
    /* The SE line sits below the numbered iPhones, newest SE first. */
    if (/\bse\b/.test(lower)) series = 9 + (match ? (parseInt(match[1], 10) - 2000) / 1000 : 0);
    if (/note/.test(lower)) series = series - 100;

    var variant = 0;
    if (/ultra/.test(lower)) variant = 6;
    else if (/pro max/.test(lower)) variant = 5;
    else if (/pro xl/.test(lower)) variant = 5;
    else if (/pro/.test(lower)) variant = 4;
    else if (/plus/.test(lower)) variant = 3;
    else if (/max/.test(lower)) variant = 3;
    else if (/\bxl\b/.test(lower)) variant = 3;
    else if (/air/.test(lower)) variant = 1;
    else if (/mini/.test(lower)) variant = -1;
    else if (/\d+e\b/.test(lower)) variant = -1;

    return series * 10 + variant;
  }

  /* { Apple: ["iPhone 17 Pro", ...], ... } -> the picker's catalogue.
     Brands nothing is declared for drop out entirely. */
  function buildCatalog(declared) {
    var catalog = [];

    BRANDS.forEach(function (brand) {
      var values = (declared && declared[brand.name]) || [];
      var models = [];
      var seen = {};

      values.forEach(function (value) {
        var text = String(value).trim();
        if (!text) return;
        var key = slug(text);
        /* Two spellings of one phone ("iPhone SE 2022" and "iPhone SE
           (2022)") share a key, so the picker lists it once. */
        if (!key || seen[key]) return;
        seen[key] = true;
        models.push({ brand: brand.name, model: label(text, brand.prefix), key: key });
      });

      if (!models.length) return;
      models.sort(function (a, b) {
        var diff = weight(b.model) - weight(a.model);
        return diff !== 0 ? diff : a.model.localeCompare(b.model);
      });
      catalog.push({ name: brand.name, models: models });
    });

    return catalog;
  }

  /* What Liquid rendered into the page — see
     snippets/peel-device-catalog.liquid. Read once per page load. */
  function declaredModels() {
    var node = document.querySelector('[data-peel-device-models]');
    if (!node) return {};
    try {
      var parsed = JSON.parse(node.textContent);
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch (error) {
      return {};
    }
  }

  var cached = null;

  window.peelDeviceMap = {
    brands: BRANDS,
    slug: slug,
    label: label,
    build: buildCatalog,
    /* The store's own catalogue, built once and reused by every picker. */
    catalog: function () {
      if (!cached) cached = buildCatalog(declaredModels());
      return cached;
    },
    /* Device entries — the objects snippets/peel-product-devices.liquid
       writes, one per phone — grouped by brand and ordered newest-first,
       ready to fill a dropdown with. The pickers all list the same
       phones in the same order, and the order is decided here rather
       than three times over.

       The entries carry their own brand and model, so the grouping is
       built from them rather than from the store catalogue: a dropdown
       showing one case line lists that line's phones, not every phone
       Peel sells. Duplicates — two products in a line cut for the same
       phone — collapse to the first of them. A phone the catalogue
       cannot place keeps its place in the list rather than dropping out
       of it, in a trailing group with no brand name. */
    group: function (entries) {
      var unique = [];
      var byKey = {};
      (entries || []).forEach(function (entry) {
        if (!entry || !entry.key || byKey[entry.key]) return;
        byKey[entry.key] = entry;
        unique.push(entry);
      });
      if (!unique.length) return [];

      var declared = {};
      unique.forEach(function (entry) {
        var brand = entry.brand || 'Apple';
        if (!declared[brand]) declared[brand] = [];
        declared[brand].push(entry.value || entry.label || entry.key);
      });

      var groups = [];
      var placed = {};
      buildCatalog(declared).forEach(function (brand) {
        var ordered = [];
        brand.models.forEach(function (model) {
          if (placed[model.key] || !byKey[model.key]) return;
          placed[model.key] = true;
          ordered.push(byKey[model.key]);
        });
        if (ordered.length) groups.push({ name: brand.name, devices: ordered });
      });

      var left = unique.filter(function (entry) {
        return !placed[entry.key];
      });
      if (left.length) groups.push({ name: '', devices: left });

      return groups;
    },
    findModel: function (key) {
      var match = null;
      this.catalog().forEach(function (brand) {
        brand.models.forEach(function (model) {
          if (model.key === key) match = model;
        });
      });
      return match;
    },
    /* A phone key with the brand words dropped, for matching a picker
       model against a storefront filter value that spells it differently:
       "samsung-galaxy-s26-ultra", "samsung-s26-ultra" and
       "galaxy-s26-ultra" all reduce to "s26-ultra". */
    normalize: function (key) {
      return String(key || '')
        .split('-')
        .filter(function (part) {
          return part && part !== 'samsung' && part !== 'galaxy' && part !== 'google' && part !== 'apple';
        })
        .join('-');
    }
  };

  if (typeof module !== 'undefined' && module.exports) module.exports = window.peelDeviceMap;
})();
